import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { Action } from '../../../../models/core/action';
import { ClientListService } from '../../../../services/client-list/client-list.service';
import { SettingsService } from '../../../../services/settings/settings.service';
import { IconButtonComponent } from '../../buttons/icon-button.component';
import { MenuComponent } from '../../panels/menu.component';
import { BaseControlComponent } from '../base-control.component';
import { SelectComponent } from '../select.component';
@Component({
  selector: 'app-client-selector',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    CheckboxModule,
    SelectComponent,
    IconButtonComponent,
    MenuComponent,
  ],
  template: `
    <div class="tw-flex tw-w-full sm:tw-w-auto tw-min-w-0">
      <app-select
        class="tw-flex-1 tw-min-w-0"
        [options]="clients"
        optionLabel="description"
        [control]="control"
        label="Client"
        [multiple]="false"
        [classes]="'sm:tw-min-w-[400px] ' + (classes || '')"
        [showHelpText]="showHelpText"
      />
      @if (!singleClient) {
        <div class="tw-pt-1">
          <app-icon-button
            ariaLabel="Client history"
            tooltip="Client history"
            icon="fa-light fa-history"
            [disabled]="history.length === 0 || control.disabled"
            (click)="systemMenu.toggle($event)"
          />
        </div>
        <app-menu
          [items]="menuItems"
          [appendTo]="'body'"
          #systemMenu
          (click)="onAction($event)"
        />
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      min-width: 0;
    }
  `,
})
export class ClientSelectorComponent
  extends BaseControlComponent
  implements OnInit
{
  private settings = inject(SettingsService);
  private clientListService = inject(ClientListService);

  @Input()
  classes: string;

  clients: ListItem[] = [];

  singleClient = false;

  history: ListItem[] = [];

  menuItems: Action[] = [];

  async ngOnInit() {
    this.clients = await this.clientListService.load();

    if (this.clientListService.isSingleClient()) {
      this.singleClient = true;
      this.control.setValue(this.clients[0]);
      this.control.disable();
    }

    this.dispose.subs = this.control.valueChanges.subscribe((client) => {
      this.updateHistory(client);
    });

    const historyJson = await this.settings.get('client-history');
    if (historyJson) {
      this.history = JSON.parse(historyJson);
    }

    this.updateMenu();
  }

  updateHistory(client: ListItem) {
    if (!client) {
      return;
    }

    let dirty = false;
    const index = this.history.findIndex((x) => x.value === client.value);
    if (index === -1) {
      // not in list, insert it in first position
      this.history.unshift(client);
      dirty = true;
    } else if (index === 0) {
      // client already in first position, do nothing
    } else if (index > 0) {
      // client is not in first position move to first
      this.history.splice(index, 1);
      this.history.unshift(client);
      dirty = true;
    }

    if (this.history.length > 5) {
      this.history.length = 5;
      dirty = true;
    }

    if (dirty) {
      this.saveHistory();
    }
  }

  updateMenu() {
    this.menuItems = [];
    for (const client of this.history) {
      this.menuItems.push({
        label: client.description,
        key: 'client',
        data: client,
      });
    }
    this.menuItems.push({ label: 'Clear history', key: 'clear' });
  }

  saveHistory() {
    const historyJson = JSON.stringify(this.history);
    this.settings.set('client-history', historyJson);
    this.updateMenu();
  }

  async onAction(action: Action) {
    if (action.key === 'clear') {
      if (this.history.length > 0) {
        this.history = [];
        this.saveHistory();
      }
    } else if (action.data && !this.control.disabled) {
      this.control.setValue(action.data);
    }
  }
}
